import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTenant } from '@/hooks/useTenant';
import { supabase } from '@/integrations/supabase/client';

export type LoyaltyDashboardSummary = {
  enabled: boolean;
  config?: {
    activated_at: string;
    points_per_real: number;
    minimum_sale_value: number;
  };
  metrics?: {
    total_distributed: number;
    active_balance: number;
    participating_customers: number;
    activated_at: string;
  };
};

export type LoyaltyParticipant = {
  customer_id: string;
  name: string | null;
  whatsapp: string | null;
  instagram: string | null;
  balance: number;
  lifetime_earned: number;
  updated_at: string;
};

export type LoyaltyParticipantsPage = {
  total: number;
  limit: number;
  offset: number;
  rows: LoyaltyParticipant[];
};

export type LoyaltyLedgerRow = {
  id: string;
  sale_id: string | null;
  points: number;
  reason: 'sale_paid' | 'sale_cancelled' | 'sale_repaid';
  created_at: string;
};

export type LoyaltyCustomerHistory = {
  customer: {
    id: string;
    name: string | null;
    whatsapp: string | null;
    instagram: string | null;
  };
  balance: number;
  lifetime_earned: number;
  total: number;
  limit: number;
  offset: number;
  rows: LoyaltyLedgerRow[];
};

export function useLoyaltyDashboard() {
  const { tenant } = useTenant();
  return useQuery({
    queryKey: ['loyalty', 'dashboard', tenant?.id],
    queryFn: async (): Promise<LoyaltyDashboardSummary> => {
      const { data, error } = await supabase.rpc('loyalty_dashboard_summary');
      if (error) throw error;
      return (data as LoyaltyDashboardSummary) ?? { enabled: false };
    },
    enabled: !!tenant?.id,
    staleTime: 1000 * 30,
  });
}

export function useLoyaltyParticipants(
  search: string,
  page: number,
  pageSize: number,
) {
  const { tenant } = useTenant();
  return useQuery({
    queryKey: ['loyalty', 'participants', tenant?.id, search, page, pageSize],
    queryFn: async (): Promise<LoyaltyParticipantsPage> => {
      const { data, error } = await supabase.rpc('loyalty_list_participants', {
        p_search: search || null,
        p_limit: pageSize,
        p_offset: page * pageSize,
      });
      if (error) throw error;
      return (data as LoyaltyParticipantsPage) ?? { total: 0, limit: pageSize, offset: 0, rows: [] };
    },
    enabled: !!tenant?.id,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 30,
  });
}

export function useCustomerLoyaltyPoints(customerId: string | null) {
  const { tenant } = useTenant();
  return useQuery({
    queryKey: ['loyalty', 'customer', tenant?.id, customerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customer_loyalty_points')
        .select('balance, lifetime_earned, updated_at')
        .eq('tenant_id', tenant!.id)
        .eq('customer_id', customerId!)
        .maybeSingle();
      if (error) throw error;
      return data ?? { balance: 0, lifetime_earned: 0, updated_at: null };
    },
    enabled: !!tenant?.id && !!customerId,
  });
}

export function useLoyaltyCustomerHistory(
  customerId: string | null,
  page: number,
  pageSize: number,
) {
  const { tenant } = useTenant();
  return useQuery({
    queryKey: ['loyalty', 'customer-history', tenant?.id, customerId, page, pageSize],
    queryFn: async (): Promise<LoyaltyCustomerHistory> => {
      const { data, error } = await supabase.rpc('loyalty_customer_history', {
        p_customer_id: customerId!,
        p_limit: pageSize,
        p_offset: page * pageSize,
      });
      if (error) throw error;
      return data as LoyaltyCustomerHistory;
    },
    enabled: !!tenant?.id && !!customerId,
    placeholderData: keepPreviousData,
  });
}
